import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { useRequireRole } from "@/lib/use-require-role";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { announcements, plans, organizations, type Announcement } from "@/mocks/saas-data";
import type { PlanId } from "@/mocks/saas-data";

export const Route = createFileRoute("/admin/announcements")({
  ssr: false,
  head: () => ({ meta: [{ title: "Admin — Announcements" }] }),
  component: Page,
});

function Page() {
  const user = useRequireRole(["superadmin"]);
  const [message, setMessage] = useState("");
  const [audienceKind, setAudienceKind] = useState<"all" | "plan" | "org">("all");
  const [audienceId, setAudienceId] = useState("");
  const [severity, setSeverity] = useState<Announcement["severity"]>("info");
  const [tick, setTick] = useState(0);

  if (!user) return null;

  const publish = () => {
    if (!message.trim()) return toast.error("Message required");
    let audience: Announcement["audience"];
    if (audienceKind === "all") audience = "all";
    else if (audienceKind === "plan") audience = { planId: audienceId as PlanId };
    else audience = { orgId: audienceId };

    announcements.unshift({
      id: `an_${Date.now()}`,
      audience,
      message: message.trim(),
      severity,
      startsAt: new Date().toISOString(),
    });
    toast.success("Announcement published");
    setMessage("");
    setTick((t) => t + 1);
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader title="Announcements" description="Broadcast banners to organizations." />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <section className="space-y-3 rounded-xl bg-card p-6 ring-1 ring-border">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Compose</h2>
          <div className="space-y-1.5"><Label>Message</Label><Input value={message} onChange={(e) => setMessage(e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>Audience</Label>
            <Select value={audienceKind} onValueChange={(v) => { setAudienceKind(v as typeof audienceKind); setAudienceId(""); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All organizations</SelectItem>
                <SelectItem value="plan">Plan</SelectItem>
                <SelectItem value="org">Specific organization</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {audienceKind === "plan" && (
            <Select value={audienceId} onValueChange={setAudienceId}>
              <SelectTrigger><SelectValue placeholder="Pick a plan" /></SelectTrigger>
              <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {audienceKind === "org" && (
            <Select value={audienceId} onValueChange={setAudienceId}>
              <SelectTrigger><SelectValue placeholder="Pick an organization" /></SelectTrigger>
              <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <div className="space-y-1.5">
            <Label>Severity</Label>
            <Select value={severity} onValueChange={(v) => setSeverity(v as Announcement["severity"])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={publish} className="w-full">Publish</Button>
        </section>
        <section className="rounded-xl bg-card ring-1 ring-border">
          <div className="border-b border-border px-5 py-4">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Published</h2>
          </div>
          <ul className="divide-y divide-border">
            {announcements.map((a) => (
              <li key={a.id} className="px-5 py-3 text-sm">
                <p className="font-medium">{a.message}</p>
                <p className="mt-1 font-mono text-[10px] uppercase text-muted-foreground">
                  {a.severity} · {typeof a.audience === "string" ? "all" : "planId" in a.audience ? `plan:${a.audience.planId}` : `org:${a.audience.orgId}`} · {new Date(a.startsAt).toLocaleDateString()}
                </p>
              </li>
            ))}
          </ul>
          <span className="hidden">{tick}</span>
        </section>
      </div>
    </div>
  );
}
